import { ChevronDownIcon, PencilSquareIcon } from '@heroicons/react/20/solid'
import { AdjustmentsHorizontalIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import { get, isEqual } from 'lodash'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useMutation, useQuery } from 'react-query'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { useTimer } from 'react-timer-hook'
import styled from 'styled-components'

import { getCurrentOrganizationId } from '@/components/App/selectors'
import Banner, { BannerTypes } from '@/components/Banner/Banner'
import { AppBuilder } from '@/components/Icons'
import Page from '@/components/Page'
import PaginationTable from '@/components/PaginationTable'
import { useRoles } from '@/components/RolesContainer'
import SharedWith from '@/components/Segments/Builder/SharedWith'
import { Contents, FlexColumn, Row } from '@/components/StyledComponents'
import Breadcrumbs from '@/components/TailwindUI/Breadcrumbs'
import Button from '@/components/TailwindUI/Button'
import ConfirmActionDialog from '@/components/TailwindUI/ConfirmDialog'
import LoadingSpinner from '@/components/TailwindUI/LoadingSpinner'
import Skeleton from '@/components/TailwindUI/Skeleton'
import useFeatures from '@/components/useFeatures'
import { WORKSHOP_AGENTS } from '@/constants/featureToggles'
import { useNotification } from '@/hooks/useNotification'
import AppEditMenu from '@/pages/SpaceAppBuilder/components/AppEditMenu/AppEditMenu'
import EditAppModal, { AppSubmission } from '@/pages/SpaceAppBuilder/components/CreateAppModal/CreateAppModal'
import NewTemplateVersionTag from '@/pages/SpaceAppBuilder/components/NewTemplateVersionTag/NewTemplateVersionTag'
import ShareOwnershipModal from '@/pages/SpaceAppBuilder/components/ShareOwnershipModal/ShareOwnershipModal'
import { StyledFlexColumn, StyledImageContainer } from '@/pages/SpaceAppBuilder/components/StyledComponents'
import { getAgentDescription, getAgentInstructions } from '@/pages/SpaceAppBuilder/utilities/agentHelpers'
import { getAppForExport } from '@/pages/SpaceAppBuilder/utilities/exportApp'
import { App, Feature, FeatureStatus, Template } from '@/pages/SpaceAppBuilder/utilities/interfaces'
import isAppOwner from '@/pages/SpaceAppBuilder/utilities/isAppOwner'
import { clearLocalStorageTriggerData } from '@/pages/SpaceAppBuilder/utilities/localStoragePreviousTriggerData'
import {
  appShareQueryOptions,
  getFetchAppQueryOptions,
  getTemplateQueryOptions
} from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppSelector } from '@/store/hooks'
import { getCurrentUser } from '@/store/selectors'
import cn from '@/utilities/cn'
import isTemplateUpdateAvailable from '@/utilities/isTemplateUpdateAvailable'

import AppAudienceTargeting from './AppAudienceTargeting'
import AppConfigurationModal from './AppConfigurationModal'
import DataSources from './DataSources'
import DefaultUI from './DefaultUI'
import Features from './Features'
import messages from './messages'

export const StyledPage = styled(Page)`
  background-color: #ffffff;
  justify-content: flex-start;
  display: flex;
`
export const AppTitle = styled.h3`
  font-weight: bold;
  justify-self: flex-start;
  font-size: 30px;
  margin-left: 15px;
  color: #4b4b4b;
`
export const AppDescription = styled.p`
  font-size: 16px;
  margin-top: 0;
  margin-bottom: 0;
  margin-left: 15px;
`
const ImportedFrom = styled.div`
  display: flex;
  align-items: center;
  margin-top: 2px;
  margin-bottom: 0;
  margin-left: 15px;
`
const ImportedFromText = styled.p`
  font-size: 12px;
`
const ImportedFromLink = styled(Link)`
  font-size: 12px;
  margin-left: 3px;
`
export const StyledContainer = styled.div`
  background-color: #ffffff;
  padding: 0 10px 0 10px;
  width: 100%;

  .breadcrumb-wrapper {
    background-color: #f3f3f4;
  }
`
export const StyledContents: any = styled(Contents)`
  background-color: #ffffff;
  padding: 20px 0 0 0;
  @media (max-width: 850px) {
    flex-wrap: wrap;
  }
`
export const StyledFlexColumnLeft = styled(FlexColumn)`
  align-items: flex-start;
  flex: 1 0 60%;
`
export const StyledFlexColumnRight = styled(FlexColumn)`
  flex: 1 0 30%;
  margin-left: 30px;
  @media (max-width: 850px) {
    margin-left: 0;
    margin-top: 10px;
  }
`
export const StyledPaginationTable = styled(PaginationTable)`
  tbody {
    tr {
      cursor: pointer;

      td {
        font-size: 14px;

        .notificationAction {
          font-size: 14px;
        }

        .template-tag {
          padding-left: 5px;
        }
      }
    }
  }
`
export const Header = styled(Row)`
  && {
    align-items: flex-start;
  }
  display: flex;
  flex-direction: column;
`
export const StyledFeatureContainer = styled.div`
  background-color: #ffffff;
  border-radius: 6px;
  display: flex;
  width: 100%;
  margin-top: 10px;
  flex-direction: column;
`
export const StyledRow = styled(Row)`
  width: 100%;
`
const AppName = styled.span`
  font-weight: bold;
`
const StyledAppBannerContainer = styled.div`
  padding-right: 6rem;
`
const StyledAppBanner = styled(Banner)`
  margin-left: 15px;
  border-radius: 20px;
`
const UpdatedStyledFlexColumn = styled(StyledFlexColumn)`
  flex: 1 0 40%;
`
const StyledNewTemplateVersionTag = styled.div`
  padding-left: 10px;
`
export const CellWrapper = ({ rowData, ...props }: any) => {
  const shouldHideRowBreak = rowData?.shouldHideRowBreak
  const removeCellBottomBorderStyling = {
    borderBottom: '1px solid white'
  }

  return <td {...props} style={shouldHideRowBreak ? removeCellBottomBorderStyling : {}} />
}

type Actions = 'deleting' | 'deactivating' | null

type ActionConfirmDialog = {
  action: Actions
  onConfirm: () => void
  id: string
  isOpen: boolean
  message: ReactNode | string
  subMessage: ReactNode | string
  cancelText: string
  confirmText: string
  loadingText: string
  disabled: boolean
}

const AppDetailsPage = () => {
  const { spaceId, appId } = useParams<{ spaceId: string; appId: string }>()
  const history = useHistory()
  const location = useLocation<{ fromImport: boolean; app: App }>()
  const roles = useRoles()
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false)

  const { notify } = useNotification()

  const features = useFeatures()
  const agentsFlagOn = !!features[WORKSHOP_AGENTS]

  const currentUser = useAppSelector(getCurrentUser)
  const organizationId = useAppSelector(getCurrentOrganizationId)

  const appQueryResult = useQuery<App>({
    ...getFetchAppQueryOptions({ spaceId, appId }),
    refetchInterval: refetchInterval
  })
  const templateId = get(appQueryResult, 'data.template.templateId', '')

  const templateQuery = useQuery<Template>({
    ...getTemplateQueryOptions({ templateId }),
    enabled: !!templateId
  })
  const template = templateQuery?.data

  const hasEditPermissions = roles.hasWorkshopAppOwnerRole(spaceId) && isAppOwner(appQueryResult?.data, currentUser?.id)
  const hasOrgAdminRole = roles.hasOrganizationAdminRole(organizationId)

  const [originalSharedList, setOriginalSharedList] = useState(appQueryResult.data?.sharedWith || [])
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [confirmAppDelete, setConfirmAppDelete] = useState<ActionConfirmDialog>({
    action: null,
    onConfirm: () => {},
    id: '',
    isOpen: false,
    message: '',
    subMessage: '',
    cancelText: '',
    confirmText: '',
    loadingText: '',
    disabled: false
  })
  const [isEditAppModalOpen, setIsEditAppModalOpen] = useState(false)
  const [isEditingApp, setIsEditingApp] = useState(false)
  const [isSharingApp, setIsSharingApp] = useState(false)
  const previousSpaceId = useRef<string>(spaceId)
  // Deployments will poll deployed features for this set period of time
  const deployPollTimeInSeconds = 15
  const time = new Date()
  time.setSeconds(time.getSeconds() + deployPollTimeInSeconds)
  const { restart } = useTimer({
    expiryTimestamp: time,
    onExpire: () => setRefetchInterval(false)
  })

  const mutateFetchApp = useMutation({
    onSuccess: () => {}
  })

  const mutateUpdateApp = useMutation({
    onSuccess: async () => {
      // Reload the App and features
      await appQueryResult.refetch()
    }
  })

  const mutateDeleteApp = useMutation({
    onSuccess: () => {
      // Reload the App and features
      appQueryResult.refetch()
    }
  })

  const mutateSharedUsersList = useMutation({
    onSuccess: () => {
      appQueryResult.refetch()
    }
  })

  const showLoadingSpinner = mutateUpdateApp.isLoading || (appQueryResult.isRefetching && isEditingApp)

  // TODO Remove this after the feature flag is removed
  useEffect(() => {
    if (previousSpaceId.current !== spaceId) {
      history.push(`/space/${spaceId}/appbuilder`)
    }
    if (appQueryResult.status === 'error') {
      const errorData: any = appQueryResult.error
      if (
        errorData.status === 404 ||
        (errorData.status === 403 && errorData.data === 'You are not permitted to access this resource')
      )
        history.push(`/space/${spaceId}/appbuilder`)
    }
  }, [spaceId, history, appQueryResult.status, appQueryResult.error])

  const onEditApplication = async (values: Partial<AppSubmission>) => {
    const partialApp: Partial<AppSubmission> = {
      name: get(values, 'name'),
      iconUrl: get(values, 'iconUrl'),
      description: get(values, 'description')
    }
    if (values.template) {
      partialApp.template = values.template
    }
    if (values.audience) {
      partialApp.audience = values.audience
    }

    if (agentsFlagOn) {
      const instructions = get(values, 'instructions')
      if (instructions) {
        const updatedDescription = { agentDescription: partialApp.description, agentInstructions: instructions }
        partialApp.description = JSON.stringify(updatedDescription)
      }
    }

    setIsEditAppModalOpen(false)
    setIsEditingApp(true)

    // @ts-expect-error
    await mutateUpdateApp.mutateAsync([
      getFetchAppQueryOptions({
        spaceId,
        appId
      }).queryKey[0],
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(partialApp)
      }
    ])
    setIsEditingApp(false)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (appQueryResult.data?.sharedWith && originalSharedList.length === 0) {
      setOriginalSharedList(appQueryResult.data.sharedWith)
    }
  })

  const onDeleteApplication = async () => {
    if (appQueryResult?.data?.features) {
      for (const feature of appQueryResult?.data?.features ?? []) {
        // delete previous trigger data values
        if (feature?.id) clearLocalStorageTriggerData(feature?.id)
      }
    }
    // @ts-expect-error
    await mutateDeleteApp.mutateAsync([
      getFetchAppQueryOptions({
        spaceId,
        appId
      }).queryKey[0],
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      }
    ])
    history.push(`/space/${spaceId}/appbuilder`)
  }

  const renderLoadingMessage = () => {
    if (mutateDeleteApp.isLoading) {
      return (
        <FormattedMessage
          id="SpaceAppBuilderDetails.DeleteAppInstance"
          defaultMessage={`Deleting ${agentsFlagOn ? 'Agent' : 'App'} Instance`}
        />
      )
    } else if (mutateUpdateApp.isLoading) {
      return (
        <FormattedMessage
          id="SpaceAppBuilderDetails.updateAppInstance"
          defaultMessage={`Updating ${agentsFlagOn ? 'Agent' : 'App'}...`}
        />
      )
    } else {
      return <FormattedMessage {...messages.updating} />
    }
  }

  const shouldShowAppSettings = () => {
    return appQueryResult.data?.features?.some(feature => feature.config?.some(config => config.isAppParameter))
  }

  const handleEdit = () => {
    setIsEditAppModalOpen(true)
  }

  const saveSharedUsersList = async (sharedWithUserIds: string[]) => {
    try {
      // @ts-expect-error
      await mutateSharedUsersList.mutateAsync([
        appShareQueryOptions({ spaceId, appId }).queryKey[0],
        {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(sharedWithUserIds || [])
        }
      ])
      if (!isEqual(originalSharedList, sharedWithUserIds)) {
        notify({
          variant: 'success',
          title: `${agentsFlagOn ? 'Agent' : 'App'} Owners Updated`,
          body: (
            <div className="text-sm text-zinc-500">
              <span>Changes have been successfully saved.</span>
            </div>
          )
        })
      }
      setOriginalSharedList(sharedWithUserIds)
    } catch {
      notify({
        variant: 'error',
        title: `${agentsFlagOn ? 'Agent' : 'App'} Owners Failed to update`,
        body: (
          <div className="text-sm text-zinc-500">
            <span>Changes have </span>
            <span className="font-bold">not</span>
            <span> been saved.</span>
          </div>
        )
      })
    }
  }

  const app = appQueryResult.data ?? location.state?.app

  const isUpdateAvailable = isTemplateUpdateAvailable({
    app,
    templates: template ? [template] : []
  })

  const isDeployingOrDeactivating = app?.features?.some(
    (f: Feature) => f.status === FeatureStatus.DEPLOYING || f.status === FeatureStatus.DEACTIVATING
  )

  useEffect(() => {
    if (isDeployingOrDeactivating && !refetchInterval) {
      setRefetchInterval(5000)
    } else if (!isDeployingOrDeactivating && refetchInterval) {
      setRefetchInterval(false)
    }
  }, [isDeployingOrDeactivating, refetchInterval])

  return (
    <StyledContainer>
      <Breadcrumbs
        className="flex px-12 py-3"
        breadcrumbs={[
          { label: `${agentsFlagOn ? 'Agents' : 'Apps'}`, path: `/space/${spaceId}/appbuilder` },
          {
            label: app ? app?.name : 'Loading...'
          }
        ]}
      />
      {appQueryResult.data && (
        <EditAppModal
          onSubmit={onEditApplication}
          isOpen={isEditAppModalOpen}
          isEditing={true}
          isLoading={mutateUpdateApp.isLoading}
          isError={mutateUpdateApp.isError}
          onClose={() => setIsEditAppModalOpen(false)}
          formValues={{
            name: appQueryResult.data.name,
            description: appQueryResult.data.description,
            iconUrl: appQueryResult.data.iconUrl,
            audience: appQueryResult.data.audience,
            template: appQueryResult.data.template,
            sharedWith: appQueryResult.data.sharedWith
          }}
          readOnly={!hasEditPermissions}
        />
      )}
      {appQueryResult.data && (
        <ShareOwnershipModal
          isOpen={isSharingApp}
          descriptor={agentsFlagOn ? 'agent' : 'app'}
          title={`${agentsFlagOn ? 'Agent' : 'App'} Owners`}
          onClose={() => setIsSharingApp(false)}
          onSubmit={userIds => {
            setIsSharingApp(false)
            saveSharedUsersList(userIds)
          }}
          sharedUserList={appQueryResult.data.sharedWith || []}
          readOnly={!(hasEditPermissions || hasOrgAdminRole)}
          appQueryResult={appQueryResult}
        />
      )}

      <StyledPage
        className="m-auto max-w-screen-xl"
        isLoading={mutateDeleteApp.isLoading}
        loadingMessage={renderLoadingMessage()}
      >
        <ConfirmActionDialog
          isOpen={confirmAppDelete?.isOpen}
          onConfirm={() => {
            confirmAppDelete.onConfirm()
            setConfirmAppDelete({ ...confirmAppDelete, isOpen: false })
          }}
          onCancel={() => setConfirmAppDelete({ ...confirmAppDelete, isOpen: false })}
          message={confirmAppDelete.message}
          subMessage={confirmAppDelete.subMessage}
          variant="error"
          cancelText={confirmAppDelete.cancelText}
          confirmText={confirmAppDelete.confirmText}
          loadingText={confirmAppDelete.loadingText}
          loading={mutateDeleteApp.isLoading}
          disabled={mutateDeleteApp?.isError}
        />
        <AppConfigurationModal
          isOpen={showConfigModal}
          readOnly={!hasEditPermissions}
          onClose={() => {
            appQueryResult.refetch()
            setShowConfigModal(false)
          }}
          features={appQueryResult.data?.features || []}
        />
        <FlexColumn>
          <Header>
            <StyledRow>
              <UpdatedStyledFlexColumn>
                {(appQueryResult.isLoading && !app) || appQueryResult.isRefetching ? (
                  <div className="flex">
                    <Skeleton className="h-[100px] w-[100px]" />
                    <div className="ml-3">
                      <Skeleton className="mt-2 h-8 w-64" />
                      <Skeleton className="mt-1 h-6 w-96" />
                      <Skeleton className="mt-1 h-6 w-60" />
                    </div>
                  </div>
                ) : (
                  <div className="flex">
                    <button className={cn({ 'cursor-default': !hasEditPermissions })} onClick={handleEdit}>
                      <StyledImageContainer
                        className="app-icon"
                        src={app?.iconUrl}
                        // @ts-expect-error
                        fallbackImage={AppBuilder}
                        useLoadingImage={false}
                      />
                    </button>
                    <div>
                      <div className="flex items-center">
                        <button
                          className={cn({ 'cursor-default': !hasEditPermissions }, 'text-left')}
                          onClick={handleEdit}
                        >
                          <AppTitle>{app?.name}</AppTitle>
                        </button>
                        {showLoadingSpinner ? (
                          <div className="ml-2 flex items-center justify-center">
                            <LoadingSpinner />
                          </div>
                        ) : hasEditPermissions ? (
                          <button onClick={handleEdit} className="ml-2 flex items-center">
                            <PencilSquareIcon className="h-5 w-5 fill-zinc-400 hover:cursor-pointer hover:fill-zinc-500" />
                          </button>
                        ) : null}
                      </div>
                      <button
                        className={cn({ 'cursor-default': !hasEditPermissions }, 'text-left')}
                        onClick={handleEdit}
                      >
                        <p className="my-0.5 ml-4 max-w-prose text-sm text-zinc-500 line-clamp-2">
                          {agentsFlagOn ? getAgentDescription(app) : app?.description}
                        </p>
                        {agentsFlagOn && (
                          <p className="my-0.5 ml-4 mt-2 max-w-prose text-sm text-zinc-500 line-clamp-2">
                            {getAgentInstructions(app)}
                          </p>
                        )}
                      </button>
                      {app?.template && (
                        <ImportedFrom>
                          <ImportedFromText>Installed from</ImportedFromText>
                          <ImportedFromLink to={`/space/${spaceId}/appbuilder/templates/${app?.template.templateId}`}>
                            {app?.template?.templateName}
                            {app?.template?.version && ` (v${app?.template?.version})`}
                          </ImportedFromLink>

                          {templateQuery.isLoading ? (
                            <div className="ml-2 mt-1">
                              <Skeleton />
                            </div>
                          ) : (
                            template &&
                            isUpdateAvailable &&
                            hasEditPermissions && (
                              <StyledNewTemplateVersionTag>
                                <NewTemplateVersionTag
                                  templateId={template.id}
                                  appId={app?.id}
                                  spaceId={spaceId}
                                  isFromAppDetailsPage={true}
                                  appName={app?.name}
                                />
                              </StyledNewTemplateVersionTag>
                            )
                          )}
                        </ImportedFrom>
                      )}
                    </div>
                  </div>
                )}
              </UpdatedStyledFlexColumn>
              {!hasEditPermissions && !appQueryResult.isLoading && (
                <StyledAppBannerContainer>
                  <StyledAppBanner
                    className={'cursor-pointer'}
                    type={BannerTypes.informational}
                    onClick={() => setIsSharingApp(true)}
                  >
                    <p className="px-2">
                      <strong>{`This ${agentsFlagOn ? 'agent' : 'app'} is read only because you do not own the ${
                        agentsFlagOn ? 'agent' : 'app'
                      }`}</strong>
                    </p>
                  </StyledAppBanner>
                </StyledAppBannerContainer>
              )}
              <div className="flex items-center pt-1 max-sm:flex-col">
                <div className="mr-4 flex items-center max-sm:mb-2">
                  <Button
                    title={`${agentsFlagOn ? 'Agent' : 'App'} Owners`}
                    onClick={() => setIsSharingApp(true)}
                    className="group flex h-8 w-7 items-center rounded-full border border-transparent bg-zinc-100 hover:border-blue-700 hover:bg-blue-100"
                  >
                    <SharedWith
                      sharedWith={appQueryResult.data?.sharedWith || []}
                      isLoading={appQueryResult.isLoading}
                      className="w-fit"
                      isOverflowHidden={false}
                    />
                    <div className="pl-1">
                      <ChevronDownIcon className="text-zinc-950 h-3 w-3 group-hover:text-blue-700" />
                    </div>
                  </Button>
                </div>
                {hasEditPermissions && shouldShowAppSettings() && (
                  // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                  <div className="mr-4 flex items-center max-sm:mb-2" onClick={() => setShowConfigModal(true)}>
                    <div
                      title="Config"
                      className="group relative w-min cursor-pointer rounded-full border border-transparent bg-zinc-100 p-0.5 hover:border-blue-700 hover:bg-blue-100"
                    >
                      <AdjustmentsHorizontalIcon
                        className="h-7 w-7 text-zinc-600 group-hover:text-blue-700"
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                )}
                <div className="mr-4 flex items-center max-sm:mb-2">
                  <div className="group flex h-8 w-8 justify-center rounded-full border border-transparent bg-zinc-100 hover:border-blue-700 hover:bg-blue-100">
                    <AppEditMenu
                      title="More"
                      icon={<EllipsisVerticalIcon className="h-7 w-7 group-hover:stroke-blue-700" title="More" />}
                      readOnly={false}
                      hasEditPermissions={hasEditPermissions}
                      onEdit={() => setIsEditAppModalOpen(true)}
                      hasUpdateAvailable={template && isUpdateAvailable}
                      onUpdateApp={() => {
                        history.push(`/space/${spaceId}/appbuilder/templates/${template?.id}`, {
                          state: {
                            fromUpdateVersion: true,
                            isFromAppDetailsPage: true,
                            appId: app?.id,
                            appName: app?.name
                          }
                        })
                      }}
                      onDelete={() => {
                        setConfirmAppDelete({
                          id: app?.id,
                          action: 'deleting',
                          isOpen: true,
                          message: (
                            <FormattedMessage
                              id="SpaceAppBuilderDetails.confirmDeleteAppTitle"
                              defaultMessage={`Delete ${agentsFlagOn ? 'Agent' : 'App'}`}
                            />
                          ),
                          subMessage: (
                            <FormattedMessage
                              {...(agentsFlagOn ? messages.confirmAgentDelete : messages.confirmAppDelete)}
                              values={{ appName: <AppName>{app?.name}</AppName> }}
                            />
                          ),
                          onConfirm: onDeleteApplication,
                          loadingText: 'Deleting...',
                          confirmText: `Delete ${agentsFlagOn ? 'Agent' : 'App'}`,
                          cancelText: `Keep ${agentsFlagOn ? 'Agent' : 'App'}`,
                          disabled: mutateDeleteApp?.isError
                        })
                      }}
                      onExport={async () => {
                        // @ts-expect-error
                        const appInstance: App = await mutateFetchApp.mutateAsync([
                          getFetchAppQueryOptions({
                            spaceId,
                            appId,
                            includeWorkflow: true
                          }).queryKey[0],
                          {
                            method: 'GET',
                            headers: {
                              'Content-Type': 'application/json'
                            }
                          }
                        ])
                        getAppForExport(appInstance)
                      }}
                      onViewLogs={() => history.push(`${location.pathname}/logs`)}
                    />
                  </div>
                </div>
              </div>
            </StyledRow>
          </Header>
          <StyledContents>
            <StyledFlexColumnLeft>
              <Features
                appQueryResult={appQueryResult}
                readOnly={!hasEditPermissions}
                onDeployFinished={() => {
                  setRefetchInterval(2000)
                  // Restart the deploy polling
                  const time = new Date()
                  time.setSeconds(time.getSeconds() + deployPollTimeInSeconds)
                  restart(time)
                }}
              />
            </StyledFlexColumnLeft>
            <StyledFlexColumnRight>
              <DataSources
                appQueryResult={appQueryResult}
                readOnly={!hasEditPermissions}
                onModalOpened={() => setRefetchInterval(false)}
              />
              <AppAudienceTargeting
                readOnly={!hasEditPermissions}
                onEditApplication={onEditApplication}
                isSavingAudience={mutateUpdateApp.isLoading || appQueryResult.isLoading || appQueryResult.isRefetching}
              />
              <DefaultUI readOnly={!hasEditPermissions} />
            </StyledFlexColumnRight>
          </StyledContents>
        </FlexColumn>
      </StyledPage>
    </StyledContainer>
  )
}

AppDetailsPage.displayName = 'AppDetailsPage'
export default AppDetailsPage
